import type { ErrorEvent, Exception, StackFrame } from '@sentry/nextjs'

/**
 * Errors thrown by scripts that in-app browsers inject into our pages, not by
 * our code. `zaloJSV2` is the Zalo in-app browser's native bridge, called
 * before it exists. They arrive through `window.onerror` with no frame of
 * ours. An injected script has no URL of its own, or carries the page URL, so
 * `denyUrls` can't separate them from our own errors on that page — they are
 * matched on the message instead.
 *
 * Anchored to the exact WebKit (`Can't find variable: X`) and V8/SpiderMonkey
 * (`X is not defined`) wording, the same shape as Sentry's own defaults for
 * `gmo` and `_AutofillCallbackHandler`. A real undefined-variable bug in our
 * code still reports, and so does a near-miss name like `zaloJSV2Extra`. Add a
 * new in-app-browser global here, not inline in either `Sentry.init`.
 */
export const IN_APP_BROWSER_SCRIPT_ERRORS: readonly RegExp[] = [
  /^Can't find variable: zaloJSV2$/,
  /^zaloJSV2 is not defined$/,
]

/**
 * WebKit stack overflows often arrive with no usable stack. Sentry then
 * synthesizes a page-URL frame, which splits one cause into an issue per URL.
 * These events are grouped into one issue tagged `stack_overflow: unattributed`,
 * at their original level. This changes only how the report groups: the overflow
 * still happened, and nothing here catches it. With no frame of ours there is
 * no call site to guard, so the grouped issue is where a recurring cause shows up.
 *
 * Our inline `beforeInteractive` scripts also run from the page URL. If that
 * issue ever shows `insertBefore`/`removeChild`/`replaceChild` frames at the
 * page URL, look at `EXTERNAL_DOM_MUTATION_GUARD_SCRIPT` first.
 */
export const UNATTRIBUTED_STACK_OVERFLOW_FINGERPRINT = ['stack-overflow', 'unattributed']

// WebKit ends the message with a period, V8 does not. Anchored: a near-miss is not this error.
const STACK_OVERFLOW_MESSAGE = /^Maximum call stack size exceeded\.?$/
// A trailing `:line:col` still names the script, as in Sentry's own Next.js chunk regex.
const SCRIPT_FILE = /\.[cm]?js(?::\d+)*$/i

/** A frame from a script file (our bundles, a third-party script we load), not the document itself. */
function isScriptFileFrame(frame: StackFrame): boolean {
  const path = (frame.filename ?? '').split(/[?#]/)[0]
  return SCRIPT_FILE.test(path)
}

/** The overflow itself reached the global handler, not a handled error linked to a different one. */
function isUnhandledStackOverflow(value: Exception): boolean {
  return (
    value.type === 'RangeError' && STACK_OVERFLOW_MESSAGE.test(value.value ?? '') && value.mechanism?.handled === false
  )
}

export function isUnattributedStackOverflow(event: Pick<ErrorEvent, 'exception'>): boolean {
  const values = event.exception?.values
  if (!values?.some(isUnhandledStackOverflow)) return false

  return values.every(value => (value.stacktrace?.frames ?? []).every(frame => !isScriptFileFrame(frame)))
}

export function groupUnattributedStackOverflow<T extends Pick<ErrorEvent, 'exception' | 'fingerprint' | 'tags'>>(
  event: T,
): T {
  if (!isUnattributedStackOverflow(event)) return event
  return {
    ...event,
    fingerprint: UNATTRIBUTED_STACK_OVERFLOW_FINGERPRINT,
    tags: { ...event.tags, stack_overflow: 'unattributed' },
  } as T
}
